import React, { useState, useEffect, useRef } from 'react'
import { selectSkillName, selectSubskills } from '../../../redux/skillSlice'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { capitalise } from '../../../utilities/constants'
import constants from '../../../utilities/constants'
import { saveUserInfo, selectUserInfo, selectUserInfoSaving } from '../../../redux/userInfoSlice'
import { fetchAnalysisData, selectAnalysisLoading } from '../../../redux/newAnalysisSlice'
import { toast } from 'react-toastify'
import { Mixpanel } from '../../../config/Mixpanel'

const LoaderThree = () => {
  const [scanPosition, setScanPosition] = useState(0)
  const [scanDirection, setScanDirection] = useState(1) // 1 for down, -1 for up
  const [statusText, setStatusText] = useState('Understanding your current level')
  const hasApiCallStarted = useRef(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const subskills = useSelector(selectSubskills)
  const skillName = useSelector(selectSkillName)
  const userInfo = useSelector(selectUserInfo)
  const isSaving = useSelector(selectUserInfoSaving)
  const isAnalysisLoading = useSelector(selectAnalysisLoading)

  // Scanning line animation - up and down continuously
  useEffect(() => {
    const interval = setInterval(() => {
      setScanPosition(prev => {
        const newPosition = prev + (scanDirection * 1.5)

        // If reached bottom, change direction to go up
        if (newPosition >= 95) {
          setScanDirection(-1)
          return 95
        }

        // If reached top, change direction to go down
        if (newPosition <= 5) {
          setScanDirection(1)
          return 5
        }

        return newPosition
      })
    }, 30)

    return () => clearInterval(interval)
  }, [scanDirection])

  useEffect(() => {
    const t1 = setTimeout(() => {
      setStatusText('Saving your details...')
    }, 1000)

    const t2 = setTimeout(() => {
      setStatusText('Analyzing your skill level...')
    }, 3000)

    const t3 = setTimeout(() => {
      setStatusText('Preparing your personalized analysis...')
    }, 5000)

    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [])

  // API call to save user info and then fetch analysis when component mounts
  useEffect(() => {
    // Track page load
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_LOADER_THREE_LOADED, {
      skill_name: skillName || '',
      user_name: userInfo?.name || '',
      user_email: userInfo?.email || '',
      session_id: userInfo?.session_id || '',
      has_user_info: !!(userInfo && userInfo.session_id && userInfo.name && userInfo.email),
      subskills_count: subskills?.length || 0,
      timestamp: new Date().toISOString()
    })

    const saveUserDataAndFetchAnalysis = async () => {
      // Prevent multiple API calls
      if (hasApiCallStarted.current) return

      if (userInfo && userInfo.session_id && userInfo.name && userInfo.email) {
        hasApiCallStarted.current = true

        try {
          // Step 1: Save user info
          await dispatch(saveUserInfo({
            session_id: userInfo.session_id,
            name: userInfo.name,
            email: userInfo.email,
            phone: userInfo.phone
          })).unwrap()

          // Step 2: Fetch analysis data
          await dispatch(fetchAnalysisData(userInfo.session_id)).unwrap()

          // Navigate to analysis page after both API calls are successful (after 6 seconds to allow loader animation)
          setTimeout(() => {
            navigate('/onboarding/analysis', { replace: true })
          }, 6000)
        } catch (error) {
          console.error('Failed to save user info or fetch analysis:', error)
          toast.error('Failed to process your data. Please try again.')
          // Navigate back to user details page on error
          setTimeout(() => {
            navigate('/onboarding/userdetails', { replace: true })
          }, 2000)
        }
      } else {
        // If no user info, redirect back to user details
        toast.error('User details not found. Please fill in your details.')
        navigate('/onboarding/userdetails', { replace: true })
      }
    }

    saveUserDataAndFetchAnalysis()
  }, [skillName, userInfo, subskills?.length]) // Remove dependencies to prevent multiple runs

  return (

    <div className='flex flex-col h-full gap-6 justify-between items-center'>
      <style>{`
        @keyframes lt-pop {
          0% { opacity: 0; transform: scale(0.6) translateY(6px); }
          60% { opacity: 1; transform: scale(1.08) translateY(0px); }
          100% { opacity: 1; transform: scale(1) translateY(0px); }
        }
        .lt-pop {
          transform-origin: center;
          animation: lt-pop 520ms cubic-bezier(0.2, 0.85, 0.2, 1) both;
          will-change: transform, opacity;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
      <div className="w-full md:max-w-[45rem] flex-1 justify-center items-center mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col">

        {/* Main Content Container */}
        <div className="max-w-2xl mx-auto text-center relative z-10">

          {/* Circular Loader with Skills */}
          <div className="mb-12 relative flex items-center justify-center">
            <div className="relative">

              {/* Outer Circle - Dark Navy */}
              <div className="w-[200px] h-[200px] rounded-full bg-[#072649]  flex items-center justify-center">

                {/* Inner Circle - Blue */}
                <div className="w-40 h-40 rounded-full bg-[#406AFF]/30 border border-[#406AFF] shadow-inner relative flex items-center justify-center">

                  {/* Central Square with Scanning Effect */}
                  <div className="w-20 h-20 bg-[#07162F] rounded-[8px] relative overflow-hidden flex items-center justify-center shadow-lg">
                    {/* Refresh Icon */}
                    <img src="https://img.learntube.ai/public-assets/personalise/ri_search-ai-2-line.webp" className='w-[24px] h-[24px]' alt="" />

                    {/* Scanning Line */}
                    <div
                      className="absolute w-full h-0.5 z-10"
                      style={{
                        top: `${scanPosition}%`,
                        backgroundImage:
                          'linear-gradient(90deg, rgba(64, 106, 255, 0) 0%, rgba(64, 106, 255, 1) 57%, rgba(64, 106, 255, 0) 100%)',
                        opacity: 1,
                        filter: 'blur(0.5px)',
                        boxShadow: '0 0 10px rgba(64, 106, 255, 0.9), 0 0 20px rgba(64, 106, 255, 0.6)'
                      }}
                    ></div>

                    {/* Additional glow effect */}
                    <div
                      className="absolute w-full h-1 z-5"
                      style={{
                        top: `${scanPosition}%`,
                        backgroundImage:
                          'linear-gradient(90deg, rgba(64, 106, 255, 0) 0%, rgba(64, 106, 255, 0.3) 57%, rgba(64, 106, 255, 0) 100%)',
                        filter: 'blur(2px)'
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Floating Skill Labels */}
              <div className="absolute inset-0 w-[200px] h-[200px]">
                {/* Strategy - Top Right */}
                <div className="absolute" style={{
                  top: '15%',
                  right: '-15%',
                }}>
                  <div
                    className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                    style={{ animationDelay: '1s' }}
                  >
                    <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                      {subskills[0]?.name}
                    </p>
                  </div>
                </div>

                {/* Product Thinking - Left */}
                <div className="absolute" style={{
                  top: '45%',
                  left: '-20%',
                }}>
                  <div
                    className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                    style={{ animationDelay: '1.5s' }}
                  >
                    <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                      {subskills[1]?.name}
                    </p>
                  </div>
                </div>

                {/* User Research - Bottom Right */}
                <div className="absolute" style={{
                  bottom: '2%',
                  right: '-5%',
                }}>
                  <div
                    className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                    style={{ animationDelay: '2s' }}
                  >
                    <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                      {subskills[2]?.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Title with Gradient Animation */}
          <div className="mt-8">
            <div className='flex flex-col'>
              <h1 className='text-center bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[130%] capitalize'>
                Understanding where you stand in {capitalise(skillName)}
              </h1>
            </div>
          </div>

          {/* Bottom Status */}
          <div className='mt-4 w-full bg-[#54B226]/15 pz-3 py-2 rounded-[12px] flex items-center justify-center gap-1'>

            <img src="https://img.learntube.ai/public-assets/personalise/Group%2023628.webp" alt="" className='w-[34px] h-[24px]' />
            <div className='text-[10px] text-white/50'>
              {(isSaving || isAnalysisLoading) ? (
                isSaving ? 'Saving your details...' : 'Analyzing your responses...'
              ) : (
                <>
                  Experts from <span className='font-bold italic'> Flipkart & Facebook </span> analysing your answers
                </>
              )}
            </div>
          </div>

        </div>
      </div>
      <div className='capitalize text-center flex flex-col gap-1 items-center justify-center text-[12px] text-[#83BEFF] font-bold mb-3'>
        <img alt="" src="https://img.learntube.ai/public-assets/personalise/Vector%20(16).webp" className='w-[20px] h-[20px] mr-1' />
        <div className='italic text-[12px] text-[#83BEFF]'>Results are based on 1L+ {capitalise(skillName)} learners</div>

      </div>
    </div>
  )
}

export default LoaderThree